import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { ackInput, ConfirmWarningButton, useAcks } from "../policy/ack";
import { isReaderKey, type ReaderBarState, ReaderView, useMobileReader } from "../reader/ReaderView";
import { useSession } from "../session";
import { Button, LinkButton } from "../ui/Button";
import { describeError, isApiError, matchedTagsOf } from "../ui/errors";
import { tagNames } from "../ui/GalleryCard";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad } from "../ui/useLoad";

/**
 * The reader of one downloaded gallery (`/read/:gid`).
 * The server decides whether it opens: a blocked gallery is refused, a warned one needs this gallery's
 * confirmation (`POLICY_ACK_REQUIRED` leads to the confirm notice, never to a silent retry).
 */
export function ReaderPage() {
  const params = useParams();
  const gid = /^[1-9][0-9]{0,14}$/.test(params.gid ?? "") ? Number(params.gid) : 0;
  // keyed by gallery: page position, mode and the history mark start afresh for another gallery
  return <Reader key={gid} gid={gid} />;
}

function Reader({ gid }: { gid: number }) {
  const { client } = useSession();
  const acks = useAcks();
  const location = useLocation();
  const navigate = useNavigate();
  const confirmed = acks.isConfirmed(gid);

  const pages = useLoad(
    () =>
      gid === 0
        ? Promise.reject(new Error("no gallery id"))
        : client.call("reader.pages", { params: { gid }, query: ackInput(acks, gid) }),
    `${gid}:${confirmed}`,
  );
  const detail = useLoad(
    () =>
      gid === 0
        ? Promise.reject(new Error("no gallery id"))
        : client.call("catalog.get", { params: { gid } }),
    String(gid),
  );
  const settings = useLoad(() => client.call("users.getSettings"), "reader-settings");
  const mobile = useMobileReader();
  const reverse = settings.data
    ? mobile
      ? settings.data.readerReverseNavigationMobile
      : settings.data.readerReverseNavigation
    : null;

  // `?p=N` opens at page N (46 Spec 3.4); anything else, or a page past the end, starts as before
  const [startPage] = useState(() => {
    const p = /^[1-9][0-9]{0,5}$/.exec(new URLSearchParams(location.search).get("p") ?? "")?.[0];
    return p === undefined ? undefined : Number(p);
  });

  // opening the reader is what the history records, once per visit
  const recorded = useRef(false);
  useEffect(() => {
    if (pages.status !== "ready" || recorded.current) return;
    recorded.current = true;
    client.call("personal.recordHistory", { body: { gid } }).catch(() => {
      // the history is a convenience: reading goes on without it
    });
  }, [pages.status, client, gid]);

  const inApp = (location.state as { inApp?: boolean } | null)?.inApp === true;
  const close = useCallback(() => {
    if (inApp) navigate(-1);
    else navigate(`/g/${gid}`, { replace: true });
  }, [inApp, navigate, gid]);

  // the reading surface handles its own keys; until it is shown, Escape still closes
  const viewing = pages.status === "ready" && pages.data.pages.length > 0;
  useEffect(() => {
    if (viewing) return;
    const onKey = (e: KeyboardEvent) => {
      if (isReaderKey(e) && e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [viewing, close]);

  // a picture that fails is most often an expired page cookie: one shared refresh, then that picture again
  const renewCookies = useCallback(() => client.refreshSession(client.credentialGeneration), [client]);

  const title = detail.data?.title ?? "";
  const bar = (view?: ReaderBarState) => (
    <header className="rd-bar" ref={view?.barRef}>
      <Button variant="text" onClick={close}>
        ← 리더 닫기
      </Button>
      <h1>{title}</h1>
      {view && (
        <>
          <span className="rd-count" role="status">
            {view.current} / {view.count}
          </span>
          <Button onClick={view.switchMode} disabled={settings.status !== "ready"}>
            {view.mode === "scroll" ? "한 쪽씩 보기" : "이어서 보기"}
          </Button>
        </>
      )}
    </header>
  );

  if (gid === 0) {
    return (
      <div className="rd">
        {bar()}
        <ErrorNote message="주소의 갤러리 번호가 올바르지 않습니다. 검색으로 찾아보세요." />
      </div>
    );
  }

  if (pages.status !== "ready") {
    const error = pages.error;
    return (
      <div className="rd">
        {bar()}
        {pages.status === "loading" && <Loading />}
        {pages.status === "error" && isApiError(error, "POLICY_ACK_REQUIRED") && (
          <div className="rd-gate">
            <div className="notice notice-warn" role="note">
              <b>경고 태그가 있습니다</b>
              <p>
                {matchedTagsOf(error).length > 0
                  ? `${tagNames(matchedTagsOf(error))} 태그가 붙은 갤러리입니다. `
                  : ""}
                확인하면 리더가 열립니다.
              </p>
              <ConfirmWarningButton gid={gid} primary />
            </div>
          </div>
        )}
        {pages.status === "error" && isApiError(error, "POLICY_BLOCKED") && (
          <div className="rd-gate">
            <div className="notice" role="note">
              <b>차단된 태그가 포함되어 있습니다</b>
              <p>이 갤러리는 받거나 읽을 수 없습니다. 제목과 태그만 볼 수 있습니다.</p>
              <LinkButton to={`/g/${gid}`}>작품 창 열기</LinkButton>
            </div>
          </div>
        )}
        {pages.status === "error" && isApiError(error, "DOWNLOAD_NOT_READY") && (
          <ErrorNote message="아직 다 받지 않은 갤러리입니다. 작품 창에서 내려받은 뒤에 읽을 수 있습니다.">
            <LinkButton to={`/g/${gid}`}>작품 창 열기</LinkButton>
          </ErrorNote>
        )}
        {pages.status === "error" &&
          !isApiError(error, "POLICY_ACK_REQUIRED") &&
          !isApiError(error, "POLICY_BLOCKED") &&
          !isApiError(error, "DOWNLOAD_NOT_READY") && (
            <ErrorNote
              message={describeError(error, {
                NOT_FOUND: "이 번호의 갤러리가 없습니다. 주소를 확인하거나 검색으로 찾아보세요.",
              })}
              onRetry={pages.reload}
            />
          )}
      </div>
    );
  }

  const list = pages.data.pages;
  if (list.length === 0) {
    return (
      <div className="rd">
        {bar()}
        <ErrorNote message="받은 파일에 쪽이 없습니다. 작품 창에서 다시 내려받으세요.">
          <LinkButton to={`/g/${gid}`}>작품 창 열기</LinkButton>
        </ErrorNote>
      </div>
    );
  }

  return (
    <ReaderView
      pages={list}
      reverse={reverse}
      startPage={startPage}
      onPageError={renewCookies}
      onClose={close}
      renderBar={bar}
      notice={
        settings.status === "error" && (
          <ErrorNote message="리더 설정을 불러오지 못했습니다." onRetry={settings.reload} />
        )
      }
    />
  );
}
